import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../hooks';
import { useAppContext } from '../../Context';
import { AttributesType, ColumnsType, SketchRequestType } from '../../types';
import { useEffect, useState } from 'react';
import { usePermissions } from '../../services';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faEye, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Grid } from '@mui/material';
import { Button } from '../../components/shared/FormElements';
import { initialStateSketch, setSketch } from '../../slices/sketchesSlice';
import DataTable from '../../components/shared/DataTable';
import { setPopConfirmation } from '../../slices/miscSlice';
import { constants } from '../../constants';

type SketchListType = 'creados' | 'por-validar' | 'con-cambios' | 'recursos-incompletos' | 'aprobados';

const statusFilters: Record<SketchListType, string[]> = {
  creados: [
    constants.sketchRequestStatus.CREADO,
    constants.sketchRequestStatus.EN_DISENO,
    constants.sketchRequestStatus.ASIGNADO,
  ],
  'por-validar': [constants.sketchRequestStatus.POR_VALIDAR],
  'con-cambios': [constants.sketchRequestStatus.CAMBIOS_SOLICITADOS],
  'recursos-incompletos': [constants.sketchRequestStatus.RECURSOS_INCOMPLETOS],
  aprobados: [constants.sketchRequestStatus.APROBADO],
};

const titlesByStatus: Record<SketchListType, string> = {
  creados: 'Bocetos Creados',
  'por-validar': 'Bocetos Por Validar',
  'con-cambios': 'Bocetos con Cambios',
  'recursos-incompletos': 'Bocetos con Recursos Incompletos',
  aprobados: 'Bocetos Aprobados',
};

const endpointByStatus: Record<SketchListType, string> = {
  creados: 'sketches/',
  'por-validar': 'sketches/onvalidate/',
  'con-cambios': 'sketches/pendingchanges/',
  'recursos-incompletos': 'sketches/incompleteresources/',
  aprobados: 'sketches/approved/',
};

const ViewSketches: React.FC<{ status: SketchListType }> = ({ status }) => {
  const { apiCall } = useAppContext();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [sketches, setSketches] = useState<SketchRequestType[]>([]);

  const {
    canView_sketchrequests,
    canView_menu_sketches,
    canView_menu_sketches_created,
    canView_menu_sketches_to_validate,
    canView_menu_sketches_changes,
    canView_menu_sketches_incomplete,
    canView_menu_sketches_approved,
    canChange_sketchrequests,
    canAdd_sketchrequests,
    canDelete_sketchrequests,
  } = usePermissions([
    'view_sketchrequests',
    'view_menu_sketches',
    'view_menu_sketches_created',
    'view_menu_sketches_to_validate',
    'view_menu_sketches_changes',
    'view_menu_sketches_incomplete',
    'view_menu_sketches_approved',
    'add_sketchrequests',
    'change_sketchrequests',
    'delete_sketchrequests',
  ]);


  useEffect(
    () => {
      getSketches();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    getSketches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const canViewMenuForStatus =
    status === 'creados'
      ? canView_menu_sketches_created
      : status === 'por-validar'
        ? canView_menu_sketches_to_validate
        : status === 'con-cambios'
          ? canView_menu_sketches_changes
          : status === 'recursos-incompletos'
            ? canView_menu_sketches_incomplete
            : status === 'aprobados'
              ? canView_menu_sketches_approved
              : false;

  if (!canView_sketchrequests && !canView_menu_sketches && !canViewMenuForStatus) {
    navigate('/');
    return null;
  }

  const getSketches = async () => {
    const endpoint = endpointByStatus[status];
    const response = await apiCall('GET', endpoint, null, '', 'Error al obtener los bocetos');
    if (response) {
      const allowedStatuses = statusFilters[status];
      const filteredSketches = response.filter((sketch: SketchRequestType) =>
        allowedStatuses.includes(sketch.status)
      );
      setSketches(filteredSketches);
    }
  };

  const decoratorEstado = {
    Creado: 'info',
    'En Diseño': 'warning',
    'Por Validar': 'secondary',
    Aprobado: 'success',
    'Cambios Solicitados': 'error',
    'Recursos Incompletos': 'warning',
    Asignado: 'success',
  };

  const columns: ColumnsType[] = [
    {
      title: 'ID',
      field: 'id',
    },
    {
      title: 'Orden',
      field: 'order_id_annotated',
    },
    {
      title: 'Cliente',
      field: 'customer_name_annotated',
    },
    {
      title: 'Diseñador',
      field: 'designer_name_annotated',
    },
    {
      title: 'Estado',
      field: 'status',
      type: 'decorator',
      decorator: decoratorEstado,
      cell_class: 'text-center',
    },
    {
      title: 'Creado por',
      field: 'created_by_username_annotated',
    },
    {
      title: 'Fecha de Creación',
      field: 'created_at',
      type: 'datetime',
    },
    {
      title: 'Fecha de Actualización',
      field: 'updated_at',
      type: 'datetime',
    },
  ];

  const actions: Array<any> = [
    canView_sketchrequests
      ? {
          icon: <FontAwesomeIcon icon={faEye} />,
          tooltip: 'Ver',
          onClick: (rowData: SketchRequestType) => {
            navigate(`/boceto/${rowData.id}`);
          },
        }
      : null,
    canChange_sketchrequests
      ? {
          icon: <FontAwesomeIcon icon={faEdit} />,
          tooltip: 'Solicitar Cambios',
          onClick: (rowData: SketchRequestType) => {
            // solicitarCambios(rowData);
          },
        }
      : null,
    canDelete_sketchrequests
      ? {
          icon: <FontAwesomeIcon icon={faTrash} />,
          tooltip: 'Eliminar',
          onClick: (rowData: SketchRequestType) => {
            dispatch(
              setPopConfirmation({
                show: true,
                message: '¿Está seguro de eliminar el boceto?',
                alertType: 'danger',
                okFunc: () => {
                  deleteBoceto(rowData.id);
                },
              })
            );
          },
        }
      : null,
  ];

  const deleteBoceto = async (id: number) => {
    const response = await apiCall(
      'DELETE',
      `sketch/${id}/`,
      null,
      'Boceto eliminado con éxito',
      'Error al eliminar el boceto'
    );
    if (response) {
      getSketches();
    }
  };

  const filterDate: Array<AttributesType> = [
    { value: 'created_at', name: 'Fecha de Creación' },
    { value: 'updated_at', name: 'Fecha de Actualización' },
  ];

  return (
    <div className='container-wrap'>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={10} className='text-left'>
          <h2 className='text-left'>{titlesByStatus[status]}</h2>
        </Grid>
        <Grid item xs={12} sm={2} className='text-right'>
          {canAdd_sketchrequests && (
            <Button
              className='btn btn-primary'
              onClick={() => {
                dispatch(setSketch(initialStateSketch.sketch));
                navigate('/boceto/crear');
              }}
            >
              Agregar
            </Button>
          )}
        </Grid>
      </Grid>
      <DataTable
        columns={columns}
        data={sketches}
        actions={actions}
        groupedOptionBy={'status'}
        filterByDateBy={filterDate}
      />
    </div>
  );
};

export default ViewSketches;
